import { toast } from "react-toastify";
import fetcher from "../../lib/fetcher";
import useSWRMutation from "swr/mutation";
import { EMarketTypes } from "../../interfaces";
import { getSpecificsOfSale } from "../../lib/utils";
import {
  EDealRequestType,
  IDealPurchaseRequest,
  IDealRansomRequest,
  IDealSaleRequest,
  TCommonWizardsSchema,
} from "./use-deal-request";
import { DeepPartial } from "../../components/shared/types";

export enum EDealRequestLinkType {
  Swap = "Swap",
  TradeIn = "TradeIn",
}

interface IDealPurchaseRequestLink extends DeepPartial<IDealPurchaseRequest> {}
interface IDealSaleRequestLink extends DeepPartial<IDealSaleRequest> {}
interface IDealRansomRequestLink extends DeepPartial<IDealRansomRequest> {}

const useDealRequestExchange = () => {
  async function dealRequest(
    url: string,
    {
      arg,
    }: {
      arg: {
        type: EDealRequestLinkType;
        data: TCommonWizardsSchema;
        marketType?: EMarketTypes;
      };
    }
  ) {
    const {
      communicationType,
      timeIntervalTypeCommunication,
      timeIntervalTypeView,
      viewRealtorParticipation,
      finance,
      aim,
      desiredLocationIds,
      rejectedLocationIds,
      selectedFacilityTypeIds,
      freeComment,
      buildingId,
      areaInSqM,
      apartmentNumber,
      streetId,
      complexId,
      facilityTypeId,
      wishPriceSell,
      offersForClient,
      facilityId,
      selectedFacilityTypeOwnFacility,
      estimatedPrice,
    } = arg.data;

    try {
      if (arg.type === EDealRequestLinkType.Swap) {
        const purchaseReq: IDealPurchaseRequestLink = {
          marketType: EMarketTypes.SECONDARY,
          type: EDealRequestType.Purchase,
          communicationType: communicationType ?? undefined,
          communicationInterval: timeIntervalTypeCommunication ?? undefined,
          purchase: {
            readyForCredit: finance ?? undefined,
            purpose: aim ?? undefined,
            viewTime: timeIntervalTypeView ?? undefined,
            viewType: viewRealtorParticipation ?? undefined,
            desiredLocationIds: desiredLocationIds ?? undefined,
            rejectedLocationIds: rejectedLocationIds ?? undefined,
            facilityTypeIds: selectedFacilityTypeIds ?? undefined,
            desiredApartmentComment: freeComment ?? undefined,
          },
        };

        const saleReq: IDealSaleRequestLink = {
          marketType: EMarketTypes.SECONDARY,
          type: EDealRequestType.Sale,
          communicationType: communicationType ?? undefined,
          communicationInterval: timeIntervalTypeCommunication ?? undefined,
          sale: {
            externalFacility: {
              streetId: streetId ?? undefined,
              complexId: complexId ?? undefined,
              areaInSqM: areaInSqM ?? undefined,
              buildingId: buildingId ?? undefined,
              typeId:
                facilityTypeId !== null
                  ? facilityTypeId
                  : selectedFacilityTypeOwnFacility !== null
                  ? selectedFacilityTypeOwnFacility
                  : undefined,
            },
            estimatedPrice: estimatedPrice ?? undefined,
            apartmentNumber: apartmentNumber
              ? String(apartmentNumber)
              : undefined,
            desiredPrice: wishPriceSell ?? undefined,
            demonstrationType: viewRealtorParticipation ?? undefined,
            // demonstrationTime:
            readinessForLowerSellPrice: offersForClient ?? undefined,
            specificsOfSale: getSpecificsOfSale(arg.data),
          },
        };

        const request = {
          linkType: EDealRequestLinkType.Swap,
          linkedRequests: [purchaseReq, saleReq],
        };

        const data = await fetcher.swr(url, {
          method: "POST",
          data: request,
        });

        return data.result;
      }

      if (
        arg.type === EDealRequestLinkType.TradeIn &&
        arg.marketType &&
        !facilityId &&
        areaInSqM &&
        buildingId &&
        apartmentNumber &&
        selectedFacilityTypeOwnFacility !== null
      ) {
        const purchaseReq: IDealPurchaseRequestLink = {
          marketType: arg.marketType,
          type: EDealRequestType.Purchase,
          purchase: {
            desiredLocationIds: desiredLocationIds ?? undefined,
            rejectedLocationIds: rejectedLocationIds ?? undefined,
            facilityTypeIds: selectedFacilityTypeIds ?? undefined,
          },
        };

        const ransomReq: IDealRansomRequestLink = {
          marketType: EMarketTypes.SECONDARY,
          type: EDealRequestType.Ransom,
          ransom: {
            externalFacility: {
              streetId: streetId ?? undefined,
              areaInSqM: areaInSqM,
              buildingId: buildingId,
              typeId: selectedFacilityTypeOwnFacility,
            },
            estimatedPrice: estimatedPrice ?? undefined,
            apartmentNumber: String(apartmentNumber),
          },
        };

        const request = {
          linkType: EDealRequestLinkType.TradeIn,
          linkedRequests: [purchaseReq, ransomReq],
        };

        const data = await fetcher.swr(url, {
          method: "POST",
          data: request,
        });

        return data.result;
      }
      if (
        arg.type === EDealRequestLinkType.TradeIn &&
        arg.marketType &&
        facilityId &&
        areaInSqM &&
        buildingId &&
        apartmentNumber
      ) {
        const purchaseReq: IDealPurchaseRequestLink = {
          marketType: arg.marketType,
          type: EDealRequestType.Purchase,
          purchase: {
            facilityId: facilityId,
          },
        };

        const ransomReq: IDealRansomRequestLink = {
          marketType: EMarketTypes.SECONDARY,
          type: EDealRequestType.Ransom,
          ransom: {
            externalFacility: {
              streetId: streetId ?? undefined,
              areaInSqM: areaInSqM,
              buildingId: buildingId,
            },
            estimatedPrice: estimatedPrice ?? undefined,
            apartmentNumber: String(apartmentNumber),
          },
        };

        const request = {
          linkType: EDealRequestLinkType.TradeIn,
          linkedRequests: [purchaseReq, ransomReq],
        };

        const data = await fetcher.swr(url, {
          method: "POST",
          data: request,
        });

        return data.result;
      }
    } catch (error: any) {
      toast.error(
        `Что-то не так. Проверьте заполненные данные. Причина: ${error}`
      );
      throw new Error(error);
    }
  }

  const { data, error, isMutating, trigger } = useSWRMutation(
    "/deal-request/link",
    dealRequest
  );

  return { data: data, error, isMutating, trigger };
};

export default useDealRequestExchange;
